import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Subtitle-style translation display queue.
 *
 * Buffers incoming translations and displays each one for a minimum time
 * proportional to its length (Netflix-style CPS timing). Prevents flashing
 * by too fast and coordinates rapid bursts.
 *
 * Reading speed is stored in localStorage under `translationWindow_readingSpeed`
 * (default 15 CPS = "Normal"). Reads live so runtime changes take effect.
 *
 * Returns:
 *   displayText:       current text on screen (drives the UI)
 *   queueSize:         number of pending translations
 *   enqueueTranslation: (text) => void  — add a translation to the queue
 *   clearQueue:        () => void       — flush queue & reset
 */
export function useTranslationQueue(initialText = "") {
  const [displayText, setDisplayText] = useState(initialText);
  const [queueSize, setQueueSize] = useState(0);

  const queueRef = useRef([]);
  const timeoutRef = useRef(null);
  const isDisplayingRef = useRef(false);
  const readingSpeedRef = useRef(15);
  const displayTextRef = useRef(initialText);
  const lastDisplayedAtRef = useRef(0);

  // Keep a ref of the current display text so the dedupe check doesn't rebuild
  // enqueueTranslation on every re-render
  useEffect(() => { displayTextRef.current = displayText; }, [displayText]);

  // Poll localStorage for reading speed so it stays in sync across windows
  useEffect(() => {
    const readSpeed = () => {
      const raw = parseInt(localStorage.getItem("translationWindow_readingSpeed") || "15", 10);
      readingSpeedRef.current = Number.isFinite(raw) && raw >= 8 && raw <= 25 ? raw : 15;
    };
    readSpeed();
    const id = setInterval(readSpeed, 1000);
    return () => clearInterval(id);
  }, []);

  const computeReadTimeMs = useCallback((text) => {
    if (!text) return 2500;
    const cps = readingSpeedRef.current || 15;
    const readMs = (text.length / cps) * 1000 + 500;
    return Math.max(2500, Math.min(12000, readMs));
  }, []);

  const showNextInQueue = useCallback(() => {
    const q = queueRef.current;
    if (q.length === 0) {
      isDisplayingRef.current = false;
      setQueueSize(0);
      return;
    }
    const next = q.shift();
    setQueueSize(q.length);
    setDisplayText(next.text);
    displayTextRef.current = next.text;
    lastDisplayedAtRef.current = Date.now();
    isDisplayingRef.current = true;
    const holdMs = computeReadTimeMs(next.text);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(showNextInQueue, holdMs);
  }, [computeReadTimeMs]);

  const enqueueTranslation = useCallback((text) => {
    if (!text) return;
    const trimmed = text.trim();
    if (!trimmed) return;

    // 10-second dedupe against currently-displayed text (safety net for any
    // hallucination that slips past the backend filter and re-arrives as an
    // identical phrase; shorter window than backend's duplicate TTL to avoid
    // dropping legitimate repeated speech).
    if (
      displayTextRef.current &&
      displayTextRef.current.trim() === trimmed &&
      Date.now() - lastDisplayedAtRef.current < 10000
    ) {
      return;
    }

    // === Live-caption compression ===
    // Keep AT MOST 1 pending item in the queue. If new translations arrive
    // while the current one is still holding, REPLACE the pending item with
    // the newest — do NOT accumulate a FIFO backlog. This prevents lag from
    // building up (previously a 60s speech burst could put the display 30s+
    // behind the audio). When the current display finishes its min hold, it
    // jumps straight to the most recent translation.
    const pending = queueRef.current[0];
    if (pending && pending.text === text) return; // already pending same text
    queueRef.current = [{ text, ts: Date.now() }];
    setQueueSize(1);

    if (!isDisplayingRef.current) {
      showNextInQueue();
    }
  }, [showNextInQueue]);

  const clearQueue = useCallback(() => {
    queueRef.current = [];
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    isDisplayingRef.current = false;
    setQueueSize(0);
  }, []);

  useEffect(() => {
    return () => {
      // Cleanup: React StrictMode double-invokes effects in dev mode which
      // fires this cleanup between mount and re-mount. If we only cleared the
      // timeout, isDisplayingRef would stay `true` forever and future
      // enqueues would silently wait for a hold that never expires.
      // Resetting isDisplayingRef here lets the re-mount (or the next
      // enqueue) safely start showing again from a clean state.
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      isDisplayingRef.current = false;
    };
  }, []);

  return { displayText, queueSize, enqueueTranslation, clearQueue };
}
